import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { TestTube, Play, FileUp, AlertTriangle, CheckCircle, XCircle } from 'lucide-react'
import { FraudRule, sampleTestData } from '@/services/mockRules'

interface RuleTestingPanelProps {
  rules: FraudRule[]
  threshold: number
}

interface TestData {
  name: string
  email: string
  phone: string
  zip: string
  ip: string
  userAgent: string
  timestamp: Date
}

interface TestResult {
  totalScore: number
  recommendation: 'approve' | 'review' | 'block'
  ruleResults: Array<{
    ruleId: string
    ruleName: string
    triggered: boolean
    score: number
    reason?: string
  }>
}

export function RuleTestingPanel({ rules, threshold }: RuleTestingPanelProps) {
  const [testData, setTestData] = useState<TestData>(sampleTestData.fraudulent)
  const [testResult, setTestResult] = useState<TestResult | null>(null)
  const [isRunning, setIsRunning] = useState(false)

  const handleSampleSelect = (sampleType: string) => {
    switch (sampleType) {
      case 'fraudulent':
        setTestData(sampleTestData.fraudulent)
        break
      case 'legitimate':
        setTestData(sampleTestData.legitimate)
        break
      case 'edgeCase':
        setTestData(sampleTestData.edgeCase)
        break
    }
    setTestResult(null)
  }

  const runTest = async () => {
    setIsRunning(true)
    setTestResult(null)

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500))

    // Simulate rule evaluation
    const activeRules = rules.filter(rule => rule.enabled)
    const ruleResults = activeRules.map(rule => {
      let triggered = false
      let score = 0
      let reason = ''

      // Simulate rule logic based on test data
      switch (rule.id) {
        case 'seq-phone':
          // Check if phone ends with sequential numbers
          const phone = testData.phone
          const lastFour = phone.slice(-4)
          if (lastFour === '3175' || lastFour === '3174' || lastFour === '3173') {
            triggered = true
            score = rule.weight
            reason = `Sequential phone pattern detected: ${phone}`
          }
          break

        case 'datacenter-ip':
          // Check if IP is from data center range
          if (testData.ip.startsWith('169.197') || testData.ip.startsWith('207.244')) {
            triggered = true
            score = rule.weight
            reason = `Data center IP detected: ${testData.ip}`
          }
          break

        case 'zip-concentration':
          // Check if zip is blacklisted
          if (['80112', '90210', '10001'].includes(testData.zip)) {
            triggered = true
            score = rule.weight
            reason = `High-risk zip code: ${testData.zip}`
          }
          break

        case 'suspicious-email':
          // Check if email domain is suspicious
          const domain = testData.email.split('@')[1]
          if (['armyspy.com', 'dayrep.com', 'rhyta.com'].includes(domain)) {
            triggered = true
            score = rule.weight
            reason = `Suspicious email domain: ${domain}`
          }
          break

        case 'velocity-pattern':
          // Simulate velocity check (random for demo)
          if (Math.random() > 0.8) {
            triggered = true
            score = rule.weight
            reason = 'Multiple rapid submissions detected'
          }
          break

        case 'ml-prediction':
          // Simulate ML model prediction
          const mlScore = Math.random()
          if (mlScore > 0.7) {
            triggered = true
            score = Math.round(rule.weight * mlScore)
            reason = `ML model confidence: ${Math.round(mlScore * 100)}%`
          }
          break
      }

      return {
        ruleId: rule.id,
        ruleName: rule.name,
        triggered,
        score,
        reason
      }
    })

    const totalScore = ruleResults.reduce((sum, result) => sum + result.score, 0)
    let recommendation: 'approve' | 'review' | 'block'
    
    if (totalScore >= threshold) {
      recommendation = 'block'
    } else if (totalScore >= 30) {
      recommendation = 'review'
    } else {
      recommendation = 'approve'
    }

    setTestResult({
      totalScore,
      recommendation,
      ruleResults
    })
    setIsRunning(false)
  }

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'approve': return 'text-green-600 bg-green-50 border-green-200'
      case 'review': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'block': return 'text-red-600 bg-red-50 border-red-200'
      default: return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const getRecommendationIcon = (recommendation: string) => {
    switch (recommendation) {
      case 'approve': return CheckCircle
      case 'review': return AlertTriangle
      case 'block': return XCircle
      default: return TestTube
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube className="w-5 h-5" />
            Rule Testing Panel
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Test your fraud detection rules against sample or custom data to validate effectiveness.
          </p>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="manual" className="space-y-4">
            <TabsList>
              <TabsTrigger value="manual">Manual Entry</TabsTrigger>
              <TabsTrigger value="samples">Sample Data</TabsTrigger>
              <TabsTrigger value="upload">Upload CSV</TabsTrigger>
            </TabsList>

            <TabsContent value="manual" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={testData.name}
                    onChange={(e) => setTestData({...testData, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={testData.email}
                    onChange={(e) => setTestData({...testData, email: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={testData.phone}
                    onChange={(e) => setTestData({...testData, phone: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip">Zip Code</Label>
                  <Input
                    id="zip"
                    value={testData.zip}
                    onChange={(e) => setTestData({...testData, zip: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ip">IP Address</Label>
                  <Input
                    id="ip"
                    value={testData.ip}
                    onChange={(e) => setTestData({...testData, ip: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="userAgent">User Agent</Label>
                  <Input
                    id="userAgent"
                    value={testData.userAgent}
                    onChange={(e) => setTestData({...testData, userAgent: e.target.value})}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="samples" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleSampleSelect('fraudulent')}>
                  <CardContent className="p-4">
                    <Badge className="bg-red-100 text-red-800 mb-2">High Risk</Badge>
                    <h4 className="font-medium">Known Fraud Sample</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Sequential phone, data center IP, suspicious domain
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleSampleSelect('legitimate')}>
                  <CardContent className="p-4">
                    <Badge className="bg-green-100 text-green-800 mb-2">Low Risk</Badge>
                    <h4 className="font-medium">Legitimate Sample</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Normal patterns, residential IP, trusted domain
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleSampleSelect('edgeCase')}>
                  <CardContent className="p-4">
                    <Badge className="bg-yellow-100 text-yellow-800 mb-2">Edge Case</Badge>
                    <h4 className="font-medium">Edge Case Sample</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Mixed signals, requires careful evaluation
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="upload" className="space-y-4">
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                <FileUp className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <h4 className="font-medium mb-1">Upload CSV File</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Upload a CSV file with lead data for batch testing
                </p>
                <Button variant="outline">
                  Choose File
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-center pt-4">
            <Button 
              onClick={runTest} 
              disabled={isRunning}
              size="lg"
              className="min-w-32"
            >
              {isRunning ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-b-transparent mr-2" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Run Test
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test Results */}
      {testResult && (
        <Card>
          <CardHeader>
            <CardTitle>Test Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Overall Result */}
            <div className={`p-4 rounded-lg border ${getRecommendationColor(testResult.recommendation)}`}>
              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = getRecommendationIcon(testResult.recommendation)
                  return <Icon className="w-6 h-6" />
                })()}
                <div>
                  <h3 className="font-semibold text-lg capitalize">
                    {testResult.recommendation === 'block' ? 'BLOCK LEAD' : 
                     testResult.recommendation === 'review' ? 'MANUAL REVIEW' : 'APPROVE LEAD'}
                  </h3>
                  <p className="text-sm opacity-90">
                    Total Risk Score: {testResult.totalScore}/{Math.max(100, threshold)}
                    {testResult.recommendation === 'block' && ` (Exceeds threshold of ${threshold})`}
                  </p>
                </div>
              </div>
            </div>

            {/* Score Breakdown */}
            <div className="space-y-4">
              <h4 className="font-medium">Rule-by-Rule Breakdown</h4>
              {testResult.ruleResults.map((result) => (
                <div key={result.ruleId} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{result.ruleName}</span>
                      {result.triggered && (
                        <Badge variant="destructive" className="text-xs">
                          TRIGGERED
                        </Badge>
                      )}
                    </div>
                    {result.reason && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {result.reason}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className={`font-bold text-lg ${result.triggered ? 'text-red-600' : 'text-green-600'}`}>
                      +{result.score}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      points
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Score Visualization */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Risk Score Progress</span>
                <span className="font-medium">{testResult.totalScore}/100</span>
              </div>
              <div className="relative">
                <Progress value={Math.min(testResult.totalScore, 100)} className="h-3" />
                {/* Threshold marker */}
                <div 
                  className="absolute top-0 w-0.5 h-3 bg-black/50"
                  style={{ left: `${threshold}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Safe (0)</span>
                <span style={{ marginLeft: `${threshold - 10}%` }}>
                  Threshold ({threshold})
                </span>
                <span>High Risk (100)</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}